import { Controller } from "@hotwired/stimulus"

// One AudioContext for every waveform on the page, created on first use.
//
// Browsers cap concurrent AudioContexts (Chrome at 6) and the presenter deck renders EVERY slide's
// players up front — a context per player would starve the clips past the cap, leaving them flat
// while the first few shaped. Decoding needs no output device, so one context serves them all and
// lives as long as the page.
let sharedContext = null

function audioContext() {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext
  if (!AudioContextClass) return null

  sharedContext ||= new AudioContextClass()
  return sharedContext
}

// Paints the waveform control set's bars (see components/_media_player_waveform).
//
// It does NOT drive playback — play/pause stays with the shared media-player controller this sits
// inside. Two jobs only:
//   1. Height — decode the clip and average its samples into one peak per bar, so the shape is the
//      real audio rather than decoration.
//   2. Progress — mark bars played as the clip advances.
//
// The bars are server-rendered; this only sets their height and toggles a class. A clip that can't
// be decoded (an unsupported codec, a network failure, a browser without AudioContext) keeps the
// flat bars the markup ships with — the player still plays, it just isn't shaped.
export default class extends Controller {
  static targets = ["bars"]
  static values = { bars: Number }

  connect() {
    this.media = this.element.closest(".app-media-player")?.querySelector("audio")
    if (!this.media) return

    this.boundProgress = () => this.paintProgress()
    this.media.addEventListener("timeupdate", this.boundProgress)
    this.media.addEventListener("seeked", this.boundProgress)
    this.media.addEventListener("ended", this.boundProgress)
    this.drawPeaks()
  }

  disconnect() {
    if (!this.media || !this.boundProgress) return

    this.media.removeEventListener("timeupdate", this.boundProgress)
    this.media.removeEventListener("seeked", this.boundProgress)
    this.media.removeEventListener("ended", this.boundProgress)
  }

  get bars() {
    return this.hasBarsTarget ? Array.from(this.barsTarget.children) : []
  }

  async drawPeaks() {
    const peaks = await this.peaks()
    if (!peaks) return

    this.bars.forEach((bar, index) => {
      bar.style.setProperty("--waveform-peak", peaks[index] ?? 0)
    })
    this.barsTarget.classList.add("is-shaped")
  }

  // One 0..1 peak per bar, or null when the clip can't be decoded.
  async peaks() {
    const source = this.element.closest(".app-media-player")?.dataset.mediaPlayerSrcValue
    const context = audioContext()
    if (!source || !context) return null

    try {
      const response = await fetch(source)
      if (!response.ok) return null

      const decoded = await context.decodeAudioData(await response.arrayBuffer())
      return this.bucket(decoded.getChannelData(0))
    } catch (_error) {
      return null // an undecodable clip keeps the flat bars; never break the slide over a visual
    }
  }

  // Average each bucket's amplitude, then normalise against the loudest so a quiet clip still fills
  // the waveform rather than flatlining.
  bucket(samples) {
    const count = this.barsValue || this.bars.length
    const size = Math.floor(samples.length / count) || 1
    const averages = Array.from({ length: count }, (_, index) => {
      let sum = 0
      for (let offset = 0; offset < size; offset += 1) sum += Math.abs(samples[index * size + offset] || 0)
      return sum / size
    })

    const loudest = Math.max(...averages)
    return loudest > 0 ? averages.map((value) => value / loudest) : averages
  }

  paintProgress() {
    const duration = this.media.duration || 0
    const played = duration > 0 ? (this.media.currentTime / duration) * this.bars.length : 0
    this.bars.forEach((bar, index) => bar.classList.toggle("is-played", index < played))
  }
}
